import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { fetchGeo, flagFromCC, type Geo } from "@/utils/geoLocation";

// Hero eyebrow with progressive personalization. Initial render matches the
// SSR-prerendered static string so crawlers and LCP stay unchanged; after
// ipapi.co resolves we fade-swap to "HEY GUIRI FROM {{city}} {{flag}}".
// We store the geo object (not the rendered string) so the greeting re-translates
// when the site language changes.
export function HeroKicker() {
  const { t, i18n } = useTranslation();
  const tk = (k: string, opts?: Record<string, unknown>) =>
    t(`marketingLanding.${k}`, opts ?? {});

  const [geo, setGeo] = useState<Geo | null>(null);

  useEffect(() => {
    let cancelled = false;
    fetchGeo().then((g) => {
      if (cancelled || !g) return;
      setGeo(g);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  // Recompute when geo OR language changes so the greeting follows the site locale.
  let personal: string | null = null;
  if (geo) {
    const flag = flagFromCC(geo.countryCode);
    if (geo.city) {
      personal = tk("hero.kickerPersonal", { city: geo.city, flag }).trim();
    } else if (geo.countryName) {
      personal = tk("hero.kickerPersonalCountryOnly", {
        country: geo.countryName,
        flag,
      }).trim();
    }
  }
  // Reference i18n.language so React re-renders on language change.
  void i18n.language;

  return (
    <span
      className="gl-kicker gl-mono gl-kicker-personal"
      style={{
        transition: "opacity 240ms ease-in-out",
        opacity: 1,
        maxWidth: "100%",
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
        display: "inline-block",
      }}
    >
      {personal ?? tk("hero.kicker")}
    </span>
  );
}
